import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, Observable } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SignalRService } from '../signalR/signalr.service';
import { StorageService } from '../api/storage.service';
import { StorageKeys } from '../../../enums/storage-keys.enum';
import {
  CreateGroupCallRequest,
  CreateGroupCallResponse,
  JoinGroupCallRequest,
  JoinGroupCallResponse,
  LeaveGroupCallRequest,
  EndGroupCallRequest,
  InviteToGroupCallRequest,
  ToggleGroupMediaRequest,
  ActiveGroupCallInfo,
  IncomingGroupCallEvent,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  GroupCallEndedEvent,
  MediaToggleEvent,
} from '../../models/group-call/group-call.model';
import { UserType } from '../../../enums/user-type.enum';

/**
 * SignalR receiver function name constants
 */
const ReceiveFunctions = {
  IncomingGroupCall: 'IncomingGroupCall',
  ParticipantJoined: 'ParticipantJoined',
  ParticipantLeft: 'ParticipantLeft',
  GroupCallEnded: 'GroupCallEnded',
  MediaToggle: 'MediaToggle',
} as const;

/**
 * Service for managing group call SignalR hub communication
 */
@Injectable({ providedIn: 'root' })
export class GroupCallHubService implements OnDestroy {
  private readonly hubName = (environment.hubs as any).groupCallHub.name;
  private readonly hubUrl = (environment.hubs as any).groupCallHub.url;
  private readonly consumerId = 'group-call-consumer';

  private readonly destroy$ = new Subject<void>();
  private connected = false;

  private readonly incomingGroupCall$ = new Subject<IncomingGroupCallEvent>();
  private latestIncomingCall: IncomingGroupCallEvent | null = null;
  private readonly recentIncoming = new Map<string, number>();
  private readonly DEDUPE_MS = 3000;
  private readonly DEDUPE_MAX_ENTRIES = 2000;
  private readonly participantJoined$ = new Subject<ParticipantJoinedEvent>();
  private readonly participantLeft$ = new Subject<ParticipantLeftEvent>();
  private readonly groupCallEnded$ = new Subject<GroupCallEndedEvent>();
  private readonly mediaToggle$ = new Subject<MediaToggleEvent>();
  private readonly activeCall$ =
    new BehaviorSubject<JoinGroupCallResponse | null>(null);
  private handlersBound = false;

  constructor(
    private signalR: SignalRService,
    private storage: StorageService
  ) {}

  /**
   * Connect to the group call hub
   */
  async connect(departmentIds: string[] = []): Promise<void> {
    if (this.connected) return;

    const user = this.storage.get(StorageKeys.USER);
    if (!user?.id || user?.userType === UserType.External) {
      return;
    }

    const params = new URLSearchParams();
    params.set('userId', user.id);

    // If no specific departments provided, join all user's departments
    if (departmentIds.length === 0 && user.userOrganizations) {
      const allDepts: string[] = [];
      user.userOrganizations.forEach((org: any) => {
        org.userDepartments?.forEach((dept: any) => {
          if (dept.departmentId) allDepts.push(dept.departmentId);
        });
      });
      departmentIds = [...new Set(allDepts)];
    }

    if (departmentIds.length > 0) {
      params.set('departmentIds', departmentIds.join(','));
    }

    await this.signalR.connect(
      this.hubName,
      this.hubUrl,
      params,
      this.consumerId
    );

    this.bindEventHandlers();
    this.connected = true;
  }

  /**
   * Disconnect from the group call hub
   */
  async disconnect(): Promise<void> {
    if (!this.connected) return;
    if (this.activeCall$.getValue()) {
      return;
    }

    await this.signalR.disconnect(this.hubName, true, this.consumerId);
    this.connected = false;
    this.handlersBound = false;
    this.recentIncoming.clear();
  }

  /**
   * Bind SignalR event handlers
   */
  private bindEventHandlers(): void {
    if (this.handlersBound) return;
    this.handlersBound = true;
    // Incoming group call invitation
    this.signalR
      .on<any>(this.hubName, ReceiveFunctions.IncomingGroupCall)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => {
        const event: IncomingGroupCallEvent = {
          callId: ev?.callId ?? ev?.CallId,
          title: ev?.title ?? ev?.Title,
          departmentId: ev?.departmentId ?? ev?.DepartmentId,
          inviterName: ev?.inviterName ?? ev?.InviterName,
          inviterBranchName:
            ev?.inviterBranchName ?? ev?.InviterBranchName ?? '',
          inviterProfessionalTitle:
            ev?.inviterProfessionalTitle ??
            ev?.InviterProfessionalTitle ??
            ev?.doctorProfessionalTitle ??
            ev?.DoctorProfessionalTitle ??
            null,
          callType: ev?.callType ?? ev?.CallType,
        };
        if (event.callId) {
          const now = Date.now();
          const last = this.recentIncoming.get(event.callId);
          if (last && now - last < this.DEDUPE_MS) {
            return;
          }
          this.recentIncoming.set(event.callId, now);
          if (this.recentIncoming.size > this.DEDUPE_MAX_ENTRIES) {
            this.recentIncoming.clear();
          }
        }
        this.latestIncomingCall = event;
        this.incomingGroupCall$.next(event);
      });

    // Participant joined
    this.signalR
      .on<any>(this.hubName, ReceiveFunctions.ParticipantJoined)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => {
        const participant = ev?.participant ?? ev?.Participant;
        const event: ParticipantJoinedEvent = {
          callId: ev?.callId ?? ev?.CallId,
          participant: {
            userId: participant?.userId ?? participant?.UserId,
            userName: participant?.userName ?? participant?.UserName,
            branchName:
              participant?.branchName ?? participant?.BranchName ?? '',
            professionalTitle:
              participant?.professionalTitle ??
              participant?.ProfessionalTitle ??
              participant?.doctorProfessionalTitle ??
              participant?.DoctorProfessionalTitle ??
              null,
            role: participant?.role ?? participant?.Role,
            isAudioMuted:
              participant?.isAudioMuted ?? participant?.IsAudioMuted,
            isVideoMuted:
              participant?.isVideoMuted ?? participant?.IsVideoMuted,
          },
        };
        this.participantJoined$.next(event);
      });

    // Participant left
    this.signalR
      .on<any>(this.hubName, ReceiveFunctions.ParticipantLeft)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => {
        const event: ParticipantLeftEvent = {
          callId: ev?.callId ?? ev?.CallId,
          userId: ev?.userId ?? ev?.UserId,
          userName: ev?.userName ?? ev?.UserName,
        };
        this.participantLeft$.next(event);
      });

    // Group call ended
    this.signalR
      .on<any>(this.hubName, ReceiveFunctions.GroupCallEnded)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => {
        const event: GroupCallEndedEvent = {
          callId: ev?.callId ?? ev?.CallId,
          endedByUserId: ev?.endedByUserId ?? ev?.EndedByUserId,
        };
        this.groupCallEnded$.next(event);
      });

    // Media toggle
    this.signalR
      .on<any>(this.hubName, ReceiveFunctions.MediaToggle)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => {
        const event: MediaToggleEvent = {
          callId: ev?.callId ?? ev?.CallId,
          userId: ev?.userId ?? ev?.UserId,
          isAudioMuted: ev?.isAudioMuted ?? ev?.IsAudioMuted,
          isVideoMuted: ev?.isVideoMuted ?? ev?.IsVideoMuted,
        };
        this.mediaToggle$.next(event);
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Hub Method Invocations
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Create a new group call
   */
  async createGroupCall(
    request: CreateGroupCallRequest
  ): Promise<CreateGroupCallResponse> {
    await this.ensureConnected();
    const response = await this.signalR.invoke(
      this.hubName,
      'CreateGroupCall',
      request
    );
    return this.mapCreateResponse(response);
  }

  /**
   * Join an existing group call
   */
  async joinGroupCall(
    request: JoinGroupCallRequest
  ): Promise<JoinGroupCallResponse> {
    await this.ensureConnected();
    const response = await this.signalR.invoke(
      this.hubName,
      'JoinGroupCall',
      request
    );
    return this.mapJoinResponse(response);
  }

  /**
   * Leave a group call
   */
  async leaveGroupCall(request: LeaveGroupCallRequest): Promise<void> {
    await this.ensureConnected();
    await this.signalR.invoke(this.hubName, 'LeaveGroupCall', request);
  }

  /**
   * End a group call (host only)
   */
  async endGroupCall(request: EndGroupCallRequest): Promise<void> {
    await this.ensureConnected();
    await this.signalR.invoke(this.hubName, 'EndGroupCall', request);
  }

  /**
   * Invite users to a group call
   */
  async inviteToGroupCall(request: InviteToGroupCallRequest): Promise<void> {
    await this.ensureConnected();
    await this.signalR.invoke(this.hubName, 'InviteToGroupCall', request);
  }

  /**
   * Toggle audio/video state
   */
  async toggleMedia(request: ToggleGroupMediaRequest): Promise<void> {
    await this.ensureConnected();
    await this.signalR.invoke(this.hubName, 'ToggleMedia', request);
  }

  /**
   * Get active group calls for a department
   */
  async getActiveGroupCalls(
    departmentId: string
  ): Promise<ActiveGroupCallInfo[]> {
    await this.ensureConnected();
    const response = await this.signalR.invoke(
      this.hubName,
      'GetActiveGroupCalls',
      departmentId
    );
    return this.mapActiveCallsResponse(response);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Event Observables
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Observable for incoming group call invitations
   */
  onIncomingGroupCall(): Observable<IncomingGroupCallEvent> {
    return this.incomingGroupCall$.asObservable();
  }

  /**
   * Get the latest incoming call event
   */
  getLatestIncomingCall(): IncomingGroupCallEvent | null {
    return this.latestIncomingCall;
  }

  /**
   * Clear the latest incoming call event
   */
  clearIncomingCall(): void {
    this.latestIncomingCall = null;
  }

  /**
   * Observable for participant joined events
   */
  onParticipantJoined(): Observable<ParticipantJoinedEvent> {
    return this.participantJoined$.asObservable();
  }

  /**
   * Observable for participant left events
   */
  onParticipantLeft(): Observable<ParticipantLeftEvent> {
    return this.participantLeft$.asObservable();
  }

  /**
   * Observable for group call ended events
   */
  onGroupCallEnded(): Observable<GroupCallEndedEvent> {
    return this.groupCallEnded$.asObservable();
  }

  /**
   * Observable for media toggle events
   */
  onMediaToggle(): Observable<MediaToggleEvent> {
    return this.mediaToggle$.asObservable();
  }

  /**
   * Observable for active group call state
   */
  getActiveCall$(): Observable<JoinGroupCallResponse | null> {
    return this.activeCall$.asObservable();
  }

  /**
   * Set the active group call state
   */
  setActiveCall(call: JoinGroupCallResponse | null): void {
    this.activeCall$.next(call);
  }

  getActiveCallValue(): JoinGroupCallResponse | null {
    return this.activeCall$.getValue();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private Helpers
  // ─────────────────────────────────────────────────────────────────────────

  private async ensureConnected(): Promise<void> {
    if (!this.connected) {
      await this.connect();
    }
  }

  private mapCreateResponse(resp: any): CreateGroupCallResponse {
    const excluded = (
      resp?.excludedParticipants ??
      resp?.ExcludedParticipants ??
      []
    ).map((p: any) => ({
      userId: p?.userId ?? p?.UserId,
      userName: p?.userName ?? p?.UserName,
      missing: p?.missing ?? p?.Missing ?? [],
    }));

    return {
      callId: resp?.callId ?? resp?.CallId,
      roomName: resp?.roomName ?? resp?.RoomName,
      token: resp?.token ?? resp?.Token,
      serverUrl: resp?.serverUrl ?? resp?.ServerUrl,
      excludedParticipants: excluded.length ? excluded : undefined,
    };
  }

  private mapJoinResponse(resp: any): JoinGroupCallResponse {
    const participants = (resp?.participants ?? resp?.Participants ?? []).map(
      (p: any) => ({
        userId: p?.userId ?? p?.UserId,
        userName: p?.userName ?? p?.UserName,
        branchName: p?.branchName ?? p?.BranchName ?? '',
        professionalTitle:
          p?.professionalTitle ??
          p?.ProfessionalTitle ??
          p?.doctorProfessionalTitle ??
          p?.DoctorProfessionalTitle ??
          null,
        role: p?.role ?? p?.Role,
        isAudioMuted: p?.isAudioMuted ?? p?.IsAudioMuted,
        isVideoMuted: p?.isVideoMuted ?? p?.IsVideoMuted,
      })
    );

    return {
      callId: resp?.callId ?? resp?.CallId,
      roomName: resp?.roomName ?? resp?.RoomName,
      token: resp?.token ?? resp?.Token,
      serverUrl: resp?.serverUrl ?? resp?.ServerUrl,
      title: resp?.title ?? resp?.Title,
      callType: resp?.callType ?? resp?.CallType,
      participants,
    };
  }

  private mapActiveCallsResponse(resp: any): ActiveGroupCallInfo[] {
    if (!Array.isArray(resp)) return [];
    return resp.map((c: any) => ({
      callId: c?.callId ?? c?.CallId,
      title: c?.title ?? c?.Title,
      departmentId: c?.departmentId ?? c?.DepartmentId,
      creatorUserName: c?.creatorUserName ?? c?.CreatorUserName,
      callType: c?.callType ?? c?.CallType,
      participantCount: c?.participantCount ?? c?.ParticipantCount ?? 0,
      startedAtUtc: c?.startedAtUtc ?? c?.StartedAtUtc,
    }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
